import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LandingFooter from "../LandingFooter";
import useFocusTrap from "../../hooks/useFocusTrap";
import "../../pages/PublicSite.css";

const LOGO_WHITE_PUBLIC_PATH = "/clearbadge-logo-white.png";

export default function LegalLayout({ title, intro, eyebrow = "Legal", children }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(true);
  const landingRootRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const mobileDrawerRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const isVerifyPage = location.pathname === "/";
  const isFindProPage =
    location.pathname.startsWith("/find-a-pro") || location.pathname.startsWith("/find-pro");
  const isPricingPage = location.pathname.startsWith("/pricing");
  const isAboutPage = location.pathname.startsWith("/about");
  const isPrivacyPage = location.pathname.startsWith("/privacy-policy");
  const isTermsPage = location.pathname.startsWith("/terms-of-service");
  const isDeleteAccountPage = location.pathname.startsWith("/delete-account");
  const isVerbalConsentPage = location.pathname.startsWith("/verbal-consent-sms");
  const isSmsTermsPage = location.pathname.startsWith("/smstos");

  useFocusTrap({
    isOpen: mobileMenuOpen,
    containerRef: mobileDrawerRef,
    onClose: () => setMobileMenuOpen(false),
    returnFocusRef: mobileMenuButtonRef,
    lockScroll: true,
  });

  useEffect(() => {
    const rootNode = landingRootRef.current;
    if (!rootNode) return undefined;
    const revealNodes = rootNode.querySelectorAll("[data-reveal]");
    if (!revealNodes.length) return undefined;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    revealNodes.forEach((node) => node.classList.remove("is-visible"));

    if (prefersReducedMotion) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const rafId = window.requestAnimationFrame(() => {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [location.pathname]);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY || 0;

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const previousY = lastScrollYRef.current;
      const delta = currentY - previousY;
      if (Math.abs(delta) < 6) return;

      if (currentY <= 24) {
        setShowFloatingNav(true);
      } else if (delta > 0) {
        setShowFloatingNav(false);
      } else {
        setShowFloatingNav(true);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) setShowFloatingNav(true);
  }, [mobileMenuOpen]);

  return (
    <div ref={landingRootRef} className="public-shell landing legal-shell clearbadge-public">
      <header className={`public-topbar landing-topbar ${showFloatingNav ? "" : "landing-topbar--hidden"}`}>
        <div className="landing-topbar-inner">
          <div className="landing-topbar-left">
            <Link to="/" className="brand-inline" aria-label="ClearBadge™ home">
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge™ logo" className="brand-logo" />
            </Link>
            <nav className="public-search-nav" aria-label="Search navigation">
              <Link
                className={`top-link ${isVerifyPage ? "active" : ""}`}
                to="/"
                data-reveal
                style={{ "--reveal-delay": "560ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Verify a License
              </Link>
              <Link
                className={`top-link ${isFindProPage ? "active" : ""}`}
                to="/find-a-pro"
                data-reveal
                style={{ "--reveal-delay": "700ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Find a Pro
              </Link>
              <Link
                className={`top-link ${isPricingPage ? "active" : ""}`}
                to="/pricing"
                data-reveal
                style={{ "--reveal-delay": "840ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Pricing
              </Link>
              <Link
                className={`top-link ${isAboutPage ? "active" : ""}`}
                to="/about"
                data-reveal
                style={{ "--reveal-delay": "980ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                About
              </Link>
            </nav>
          </div>
          <div className="public-links landing-topbar-right">
            <Link
              className="top-link"
              to="/login"
              data-reveal
              style={{ "--reveal-delay": "1120ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              Sign In
            </Link>
            <Link
              className="primary nav-cta mobile-signup"
              to="/login?mode=signup"
              data-reveal
              style={{ "--reveal-delay": "1260ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              Get Verified
            </Link>
            <button
              ref={mobileMenuButtonRef}
              className="icon-btn mobile-menu-btn"
              type="button"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="legal-mobile-menu"
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              <span className="burger-line" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>
      <div className={`public-mobile-overlay ${mobileMenuOpen ? "open" : ""}`}>
        <button
          type="button"
          className="public-mobile-overlay-dismiss"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
        />
        <div
          id="legal-mobile-menu"
          ref={mobileDrawerRef}
          className={`public-mobile-drawer ${mobileMenuOpen ? "open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="public-mobile-header">
            <Link to="/" className="brand-inline" aria-label="ClearBadge™ home" onClick={() => setMobileMenuOpen(false)}>
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge™ logo" className="brand-logo" />
            </Link>
            <button className="icon-btn mobile-drawer-close" type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
              <span className="mobile-drawer-close-icon" aria-hidden="true" />
            </button>
          </div>
          <div className="public-mobile-links">
            <Link to="/login" className="public-mobile-link" onClick={() => setMobileMenuOpen(false)}>
              Sign In
            </Link>
            <Link
              to="/login?mode=signup"
              className="public-mobile-link primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              Get Verified
            </Link>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">TOOLS</span>
              <Link
                to="/"
                className={`public-mobile-link secondary ${isVerifyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Verify a License</span>
              </Link>
              <Link
                to="/find-a-pro"
                className={`public-mobile-link secondary ${isFindProPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Find a Pro</span>
              </Link>
            </div>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">RESOURCES</span>
              <Link
                to="/about"
                className={`public-mobile-link secondary ${isAboutPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">About</span>
              </Link>
              <Link
                to="/pricing"
                className={`public-mobile-link secondary ${isPricingPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Pricing</span>
              </Link>
              <Link
                to="/privacy-policy"
                className={`public-mobile-link secondary ${isPrivacyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Privacy Policy</span>
              </Link>
              <Link
                to="/terms-of-service"
                className={`public-mobile-link secondary ${isTermsPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Terms of Service</span>
              </Link>
              <Link
                to="/delete-account"
                className={`public-mobile-link secondary ${isDeleteAccountPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Delete Account</span>
              </Link>
              <a
                href="/verbal-consent-sms"
                className={`public-mobile-link secondary ${isVerbalConsentPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Verbal Consent SMS</span>
              </a>
              <a
                href="/smstos"
                className={`public-mobile-link secondary ${isSmsTermsPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">SMS Terms and Privacy</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <section className="hero legal-hero">
        <div className="hero-inner legal-hero-inner">
          {eyebrow && (
            <p className="eyebrow legal-eyebrow" data-reveal style={{ "--reveal-delay": "0ms", "--reveal-x": "-24px", "--reveal-y": "0px" }}>
              {eyebrow}
            </p>
          )}
          <h1 data-reveal style={{ "--reveal-delay": "120ms", "--reveal-x": "-24px", "--reveal-y": "0px" }}>
            {title}
          </h1>
          {intro && (
            <p className="legal-hero-text" data-reveal style={{ "--reveal-delay": "240ms", "--reveal-x": "-24px", "--reveal-y": "0px" }}>
              {intro}
            </p>
          )}
        </div>
      </section>

      <main className="legal-main" id="main-content">
        <div
          className="landing-container legal-container"
          data-reveal
          style={{ "--reveal-delay": "420ms", "--reveal-x": "0px", "--reveal-y": "0px", "--reveal-scale": "1" }}
        >
          {children}
        </div>
      </main>
      <LandingFooter className="legal-footer" />
    </div>
  );
}



